import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import clsx from 'clsx';

import DeleteModal from '@atom/components/common/DeleteModal';
import EditRoleModal from '@atom/components/common/editRoleModal/EditRoleModal';
import RoleDuplicateModal from '@atom/components/common/roleDuplicateModal/RoleDuplicateModal';
import { DELETE_ROLE } from '@atom/graph/role';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton } from '@atom/mui';
import {
  ArrowBackIcon,
  ContentCopyIcon,
  DeleteIcon,
  EditIcon,
  GroupIcon,
  SettingsIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import RoleContext, { View } from '../RoleContext';

import classes from './roleHeader.module.css';

const RoleHeader = () => {
  const [, { navigate, backDestination }] = useNavigation();
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const { role, view: activeView, setView } = useContext(RoleContext);

  const [deleteRole, { loading: loadingDelete }] = useMutation(DELETE_ROLE);

  const views = [
    {
      value: View.POLICIES,
      label: 'policies',
      icon: SettingsIcon,
    },
    {
      value: View.ASSIGNEES,
      label: 'assigned to',
      icon: GroupIcon,
    },
  ];

  const handleDelete = async () => {
    await deleteRole({
      variables: { id: role.id },
    });

    navigate('/team');
  };

  return (
    <>
      <div className={classes.headerContainer}>
        <div className={classes.topBar}>
          <div className={classes.titleContainer}>
            <IconButton
              data-cy="roleDetailBackButton"
              onClick={() => navigate(backDestination('/team'))}
            >
              <Icon color={colors.neutral.white} icon={ArrowBackIcon} />
            </IconButton>
            <div className={classes.title}>{role.name}</div>
          </div>
          {hasRolePermissions(ROLE_SETS.ORG_ADMIN) && (
            <div>
              {!role.isDefault && (
                <IconButton
                  data-cy="roleDetailEditButton"
                  onClick={() => setEditModalOpen(true)}
                >
                  <Icon color={colors.neutral.white} icon={EditIcon} />
                </IconButton>
              )}
              <IconButton
                data-cy="roleDetailDuplicateButton"
                onClick={() => setDuplicateModalOpen(true)}
              >
                <Icon color={colors.neutral.white} icon={ContentCopyIcon} />
              </IconButton>
              {!role.isDefault && (
                <IconButton
                  data-cy="roleDetailDeleteButton"
                  onClick={() => setDeleteModalOpen(true)}
                >
                  <Icon color={colors.neutral.white} icon={DeleteIcon} />
                </IconButton>
              )}
            </div>
          )}
        </div>
        <div className={classes.description}>{role.description}</div>
        <div className={classes.navigationBar}>
          {views.map(view => {
            return (
              <div
                key={view.value}
                data-cy={`roleDetailNavTab-${view.value}`}
                onClick={() => setView(view.value)}
                className={clsx(
                  classes.iconContainer,
                  activeView === view.value && classes.active,
                )}
              >
                <IconButton
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  sx={{
                    ml: 1,
                    '&.MuiButtonBase-root:hover': {
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  <Icon color={colors.neutral.white} icon={view.icon} />
                </IconButton>
                <div className={classes.label}>{view.label}</div>
              </div>
            );
          })}
        </div>
      </div>
      <EditRoleModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        role={role}
      />
      <RoleDuplicateModal
        open={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        role={role}
      />
      <DeleteModal
        title="Delete Role?"
        content="Deleting a role will delete all of its policies and remove it from all assigned users and user groups. Are you sure?"
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        loading={loadingDelete}
      />
    </>
  );
};

export default RoleHeader;
